/**
 * The tooltip of a trigger. A page holds many closed tooltips, and they render again with each
 * render of their parents, so a closed tooltip is only its trigger: the Radix tooltip mounts
 * when it opens and goes when the close animation ends. Radix positions it at the trigger's node
 * through a virtual anchor, so the Radix tree does not wrap the trigger, and the trigger keeps
 * its node, its focus and its state.
 *
 * The trigger logic is ours, decided in `tooltipPolicy.ts`: the open delay, the skip window of
 * the provider, and the close on a pointer down, a click, a blur or a scroll.
 *
 * A tooltip must not outlive the hover that opened it. A pointer that moves away sends a leave,
 * but a pointer can stop being over the trigger without moving: a modal Radix layer puts
 * `pointer-events: none` on the body, and a menu that closes gives the focus back to its
 * trigger. So an open tooltip checks its premise every `HOVER_CHECK_MS`: the trigger matches
 * `:hover`, or has a focus that a key moved. The same check gates the open.
 */

import type { ReactElement, ReactNode } from 'react';
import { useContext, useEffect, useEffectEvent, useId, useReducer, useRef, useState } from 'react';

import {
    type TooltipEvent, TooltipEventKind, type TooltipInput, TooltipMode, type TooltipState,
} from '@/types/tooltip';

import {
    createTooltipSettings, DEFAULT_TOOLTIP_DELAY_MS, DEFAULT_TOOLTIP_SKIP_MS, TooltipContext,
    TooltipSettingsContext,
} from './tooltipContext';
import { INITIAL_TOOLTIP_STATE, reduceTooltip } from './tooltipPolicy';

const HOVER_CHECK_MS = 150;

// Chrome's own `:focus-visible` also matches a trigger that a menu gives the focus back to after
// a mouse pick; the keyboard mark of app/keyboardFocus.ts does not.
const KEYBOARD_FOCUS_SELECTOR = '[data-keyboard-focus] :focus-visible';

function isTriggerHoveredOrFocused(trigger: HTMLElement | null): boolean {
    if (trigger === null) {
        return true;
    }
    try {
        return trigger.matches(':hover') || trigger.matches(KEYBOARD_FOCUS_SELECTOR);
    } catch {
        return true;
    }
}

interface TooltipAction {
    event: TooltipEvent;
    input: TooltipInput;
}

function reduce(state: TooltipState, action: TooltipAction): TooltipState {
    return reduceTooltip(state, action.event, action.input);
}

export const Tooltip = ({
    mode = TooltipMode.Hover,
    delayMs,
    children,
}: {
    mode?: TooltipMode;
    delayMs?: number;
    children: ReactNode;
}): ReactElement => {
    const providerSettings = useContext(TooltipSettingsContext);
    // A tooltip outside a provider, on a page or in a test, keeps its own skip window.
    const [ownSettings] = useState(() => providerSettings ??
        createTooltipSettings(DEFAULT_TOOLTIP_DELAY_MS, DEFAULT_TOOLTIP_SKIP_MS));
    const settings = providerSettings ?? ownSettings;
    const [state, dispatch] = useReducer(reduce, INITIAL_TOOLTIP_STATE);
    const contentId = useId();
    const triggerRef = useRef<HTMLElement | null>(null);

    const send = (event: TooltipEvent): void => {
        dispatch({
            event,
            input: {
                mode,
                isHoveredOrFocused: (): boolean => isTriggerHoveredOrFocused(triggerRef.current),
                isOpenDelayed: settings.window.isOpenDelayed(Date.now()),
            },
        });
    };
    const sendFromEffect = useEffectEvent(send);

    useEffect(() => {
        sendFromEffect({ kind: TooltipEventKind.ModeChanged });
    }, [mode]);

    useEffect(() => {
        if (!state.isOpen) {
            return undefined;
        }
        settings.window.markOpen(
            contentId, (): void => sendFromEffect({ kind: TooltipEventKind.OutsideClose }));
        return () => settings.window.markClosed(contentId, Date.now());
    }, [state.isOpen, settings, contentId]);

    useEffect(() => {
        if (!state.isTimerRunning) {
            return undefined;
        }
        const timer = setTimeout(
            () => sendFromEffect({ kind: TooltipEventKind.TimerFired }),
            delayMs ?? settings.delayMs,
        );
        return () => clearTimeout(timer);
    }, [state.isTimerRunning, delayMs, settings]);

    useEffect(() => {
        if (!state.isPointerDown) {
            return undefined;
        }
        const onPointerUp = (): void => sendFromEffect({ kind: TooltipEventKind.PointerUp });
        document.addEventListener('pointerup', onPointerUp);
        return () => document.removeEventListener('pointerup', onPointerUp);
    }, [state.isPointerDown]);

    useEffect(() => {
        if (!state.isOpen) {
            return undefined;
        }
        const check = (): void => sendFromEffect({ kind: TooltipEventKind.HoverCheck });
        const close = (): void => sendFromEffect({ kind: TooltipEventKind.OutsideClose });
        // A scroll moves the trigger away from the tooltip.
        const closeOnScroll = (event: Event): void => {
            if (event.target instanceof Node && event.target.contains(triggerRef.current)) {
                close();
            }
        };
        const timer = setInterval(check, HOVER_CHECK_MS);
        window.addEventListener('blur', close);
        document.addEventListener('visibilitychange', close);
        window.addEventListener('scroll', closeOnScroll, { capture: true });
        return () => {
            clearInterval(timer);
            window.removeEventListener('blur', close);
            document.removeEventListener('visibilitychange', close);
            window.removeEventListener('scroll', closeOnScroll, { capture: true });
        };
    }, [state.isOpen]);

    return (
        <TooltipContext.Provider value={{ state, mode, contentId, triggerRef, send }}>
            {children}
        </TooltipContext.Provider>
    );
};
